import styled, { css, keyframes } from "styled-components";
import type {
  DefaultTheme,
  FlattenInterpolation,
  ThemeProps,
} from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";

export type V3ButtonVariant = "primary" | "outline" | "ghost" | "destructive";
export type V3ButtonSize = "sm" | "md" | "lg";

type ThemedCSS = FlattenInterpolation<ThemeProps<DefaultTheme>>;

const ctaFill = (theme: DefaultTheme): string =>
  theme.colors.ui.interactive.cur8Brand;

const ctaLabel = (theme: DefaultTheme): string => theme.colors.ui.text.onBrand;

/** Stroke and label of the secondary CTA, which carries no fill. */
const ctaOutline = (theme: DefaultTheme): string =>
  theme.colors.ui.interactive.cur8Brand;

const VARIANT_STYLES: Record<V3ButtonVariant, ThemedCSS> = {
  primary: css`
    background: ${({ theme }) => ctaFill(theme)};
    color: ${({ theme }) => ctaLabel(theme)};
    border-color: transparent;
  `,
  outline: css`
    background: ${({ theme }) => theme.colors.ui.overlay.transparent};
    color: ${({ theme }) => ctaOutline(theme)};
    border-color: ${({ theme }) => ctaOutline(theme)};
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => ctaOutline(theme)};
    border-color: transparent;
  `,
  destructive: css`
    background: ${({ theme }) => theme.colors.ui.interactive.destructive};
    color: ${({ theme }) => theme.colors.ui.text.onFill};
    border-color: transparent;
  `,
};

const SIZE_STYLES: Record<V3ButtonSize, ThemedCSS> = {
  sm: css`
    min-height: ${() => 40}px;
    padding: 0 ${({ theme }) => theme.space.card.inset}px;
    ${typographyCSS("label.sm")}
  `,
  md: css`
    min-height: ${({ theme }) => theme.size.control.md}px;
    padding: 0 ${({ theme }) => theme.space["2xl"]}px;
    ${typographyCSS("label.md")}
  `,
  lg: css`
    min-height: ${({ theme }) => theme.size.button.lg}px;
    padding: 0 ${({ theme }) => theme.space["2xl"]}px;
    ${typographyCSS("label.md")}
  `,
};

interface StyledButtonProps {
  $variant: V3ButtonVariant;
  $size: V3ButtonSize;
  $fullWidth: boolean;
}

export const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm}px;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  border-style: solid;
  border-width: ${({ theme }) => theme.stroke.md}px;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.motion.duration.instant}ms
    ${({ theme }) => theme.motion.easing.standard};

  ${({ $size }) => SIZE_STYLES[$size]}
  ${({ $variant }) => VARIANT_STYLES[$variant]}

  &:hover:not(:disabled) {
    opacity: ${({ theme }) => 0.8};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.stroke.focus}px solid
      ${({ theme }) => theme.colors.ui.border.focus};
    outline-offset: ${({ theme }) => theme.stroke.focus}px;
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.ui.interactive.disabled};
    color: ${({ theme }) => theme.colors.ui.interactive.disabledText};
    border-color: transparent;
    cursor: not-allowed;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const ButtonSpinner = styled.span`
  flex: none;
  width: ${({ theme }) => theme.size.icon.xs}px;
  height: ${({ theme }) => theme.size.icon.xs}px;
  border: ${({ theme }) => theme.stroke.selected}px solid currentColor;
  border-top-color: transparent;
  border-radius: ${({ theme }) => theme.radius.full}px;
  animation: ${spin} ${({ theme }) => theme.motion.duration.slow}ms
    ${({ theme }) => theme.motion.easing.linear} infinite;
`;

export const ButtonIcon = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
`;

export const ButtonLabel = styled.span`
  &:empty {
    display: none;
  }
`;
